import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  Users, Shield, Check, X, Loader2, Plus, 
  UserPlus, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const normalizeRole = (role) => {
  if (!role) return '';
  const r = String(role).trim().toLowerCase();
  if (r === 'admin' || r === 'system_admin' || r === 'system-admin' || r === 'system administrator') return 'admin';
  return r;
};

const roleBadges = {
  admin: 'badge-error',
  dietitian: 'badge-info',
  office_admin: 'badge-success'
};

const UsersRoles = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    role: 'dietitian',
    password: '',
    confirmPassword: '',
    is_active: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load users');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      full_name: '', username: '', email: '',
      role: 'dietitian', password: '', confirmPassword: '',
      is_active: true
    });
    setEditingUser(null);
  };

  const openEditModal = (user) => {
    setFormData({
      full_name: user.full_name || '',
      username: user.username || '',
      email: user.email || '',
      role: normalizeRole(user.role),
      password: '',
      confirmPassword: '',
      is_active: user.is_active !== false
    });
    setEditingUser(user);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      if (editingUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        delete payload.confirmPassword;
        await api.put(`/auth/users/${editingUser.id}`, payload);
        toast.success('User updated successfully');
      } else {
        const payload = { ...formData };
        delete payload.confirmPassword;
        await api.post('/auth/users', payload);
        toast.success('User created successfully');
      }
      setShowAddModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
    setSubmitting(false);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.put(`/auth/users/${id}`, {
        is_active: !currentStatus
      });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <Users size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Users & Roles</h1>
            <p className="text-sm text-neutral-gray">Manage system users and permissions</p>
          </div>
        </div>

        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add User</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: users.length, color: 'bg-brand-blue-50 text-brand-blue-800', icon: Users },
          { label: 'Active Users', value: users.filter(u => u.is_active).length, color: 'bg-brand-green-50 text-brand-green-800', icon: Check },
          { label: 'System Admins', value: users.filter(u => normalizeRole(u.role) === 'admin').length, color: 'bg-brand-red-50 text-brand-red-800', icon: Shield },
          { label: 'Dietitians', value: users.filter(u => normalizeRole(u.role) === 'dietitian').length, color: 'bg-brand-purple-50 text-brand-purple-800', icon: Users }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-gray">{stat.label}</p>
                  <p className="text-2xl font-bold text-neutral-dark mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.color.split(' ')[0]}`}>
                  <Icon size={24} className={stat.color.split(' ')[1]} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-medium overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-light">
                <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">User</th>
                <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Username</th>
                <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Role</th>
                <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Status</th>
                <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-neutral-gray">No users found</td>
                </tr>
              ) : users.map(user => (
                <tr key={user.id} className="border-t border-neutral-medium hover:bg-neutral-light transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-brand-green-100 flex items-center justify-center">
                        <span className="text-brand-green-800 font-medium text-sm">
                          {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-dark">{user.full_name}</p>
                        <p className="text-xs text-neutral-gray">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-gray">{user.username}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${roleBadges[normalizeRole(user.role)] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                      {normalizeRole(user.role).replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      user.is_active ? 'badge-success' : 'badge-error'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-brand-blue hover:text-brand-blue-700 text-sm font-medium"
                        title="Edit user"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(user.id, user.is_active)}
                        className={`text-sm font-medium ${
                          user.is_active ? 'text-brand-red hover:text-brand-red-700' : 'text-brand-green hover:text-brand-green-700'
                        }`}
                        title={user.is_active ? 'Deactivate user' : 'Activate user'}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-brand-red">*</span></label>
                <input type="text" value={formData.full_name} onChange={(e) => setFormData(p => ({...p, full_name: e.target.value}))} required
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Username <span className="text-brand-red">*</span></label>
                  <input type="text" value={formData.username} onChange={(e) => setFormData(p => ({...p, username: e.target.value}))} required
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-brand-red">*</span></label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({...p, email: e.target.value}))} required
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Role <span className="text-brand-red">*</span></label>
                <select value={formData.role} onChange={(e) => setFormData(p => ({...p, role: e.target.value}))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white">
                  <option value="admin">Admin</option>
                  <option value="dietitian">Dietitian</option>
                  <option value="office_admin">Office Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <div className="flex items-center space-x-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData(p => ({...p, is_active: !p.is_active}))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.is_active ? 'bg-brand-green' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className={`text-sm font-medium ${formData.is_active ? 'text-brand-green' : 'text-neutral-gray'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password {!editingUser && <span className="text-brand-red">*</span>}
                  </label>
                  <input type="password" value={formData.password} onChange={(e) => setFormData(p => ({...p, password: e.target.value}))}
                    required={!editingUser}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm Password {!editingUser && <span className="text-brand-red">*</span>}
                  </label>
                  <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData(p => ({...p, confirmPassword: e.target.value}))}
                    required={!editingUser}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-brand-red mt-1">Passwords do not match</p>
              )}
              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                  {submitting ? <span className="flex items-center space-x-2"><Loader2 size={16} className="animate-spin" /><span>Saving...</span></span>
                    : editingUser ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersRoles;